import { useState } from "react"
import {
  Form,
  InputGroup,
  Button,
  Modal,
  Spinner,
  Alert,
  Table,
} from "react-bootstrap"
import useFetch from "../../../_fetch"

interface Product {
  name: string
  ean?: string
}

interface Props {
  onProductsProcessed: (products: Product[]) => void
}

export default function ProductFetcher({ onProductsProcessed }: Props) {
  const { doFetch } = useFetch()
  const [linkInput, setLinkInput] = useState<string>("")
  const [showModal, setShowModal] = useState(false)
  const [foundProducts, setFoundProducts] = useState<Product[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [modalError, setModalError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [pageCount, setPageCount] = useState(1)

  const handleOpenModal = () => {
    setShowModal(true)
    setFoundProducts([])
    setModalError(null)
  }

  const handleFetchProducts = async () => {
    setIsLoading(true)
    setModalError(null)
    setFoundProducts([])

    try {
      const res = await doFetch('/eb-pristjek/fetch-products-by-url', {
        url: linkInput,
        pages: pageCount,
      })

      if(res.status >= 400) throw new Error(await res.text())

      const productsStr: string[] = await res.json()
      const products: Product[] = productsStr.map((p) => ({name: p}))

      setFoundProducts(products)
    } catch (err: any) {
      setModalError(err.message || "Ukendt fejl opstod.")
    } finally {
      setIsLoading(false)
    }
  }

  const handleChangeProductName = (index: number, newName: string) => {
    const updated = [...foundProducts]
    updated[index].name = newName
    setFoundProducts(updated)
  }

  const handleDeleteProduct = (index: number) => {
    setFoundProducts(foundProducts.filter((_, i) => i !== index))
  }

  const handleAddProduct = () => {
    setFoundProducts([...foundProducts, { name: "" }])
  }

  const handleSubmitProducts = async () => {
    setIsSubmitting(true)
    setModalError(null)

    try {
      const names = foundProducts.map((p) => p.name.trim()).filter((n) => n)
      if (names.length === 0) throw new Error("Ingen produktnavne at sende.")

      const res = await doFetch('/eb-pristjek/get-products-by-name', {
        productNames: names,
      })

      if(res.status >= 400) throw new Error(await res.text())
      
      const products: Product[] = await res.json()

      onProductsProcessed(products)
      setShowModal(false)
    } catch (err: any) {
      setModalError(err.message || "Fejl under afsendelse.")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <Button variant="success" onClick={handleOpenModal}>
        Find produkter
      </Button>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Indhent produkter</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Form.Group className="mb-3" controlId="modalLinkInput">
                <Form.Label>Indsæt link og antal sider</Form.Label>
                <InputGroup>
                    <Form.Control
                    type="text"
                    placeholder="https://..."
                    value={linkInput}
                    onChange={(e) => setLinkInput(e.target.value)}
                    size="sm"
                    />
                    <Form.Select
                    size="sm"
                    style={{ maxWidth: "100px" }}
                    value={pageCount}
                    onChange={(e) => setPageCount(Number(e.target.value))}
                    >
                    {[...Array(10)].map((_, i) => (
                        <option key={i + 1} value={i + 1}>
                        {i + 1} side{i !== 0 && "r"}
                        </option>
                    ))}
                    </Form.Select>
                    <Button
                    variant="primary"
                    onClick={handleFetchProducts}
                    disabled={!linkInput.trim() || isLoading}
                    size="sm"
                    >
                    {isLoading ? (
                        <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Henter...
                        </>
                    ) : (
                        "Hent"
                    )}
                    </Button>
                </InputGroup>
            </Form.Group>

            {modalError ? (
                <Alert variant="danger">{modalError}</Alert>
            ) : null}

            <div style={{ maxHeight: "500px", minHeight: "500px", overflowY: "auto" }}>
                <Table striped bordered hover size="sm" className="h-100">
                    <thead>
                    <tr>
                        <th>Produktnavn</th>
                        <th style={{ width: "80px" }}>Handling</th>
                    </tr>
                    </thead>
                    <tbody>
                    {foundProducts.map((product, index) => (
                        <tr key={index}>
                        <td className="p-0">
                            <Form.Control
                            type="text"
                            value={product.name}
                            onChange={(e) =>
                                handleChangeProductName(index, e.target.value)
                            }
                            />
                        </td>
                        <td className="d-flex justify-content-center">
                            <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDeleteProduct(index)}
                            >
                            Slet
                            </Button>
                        </td>
                        </tr>
                    ))}
                    </tbody>
                </Table>
            </div>
            <p className="text-muted mt-2">
                <strong>{foundProducts.length}</strong> API request
                {foundProducts.length !== 1 && "s"} mod Datafeedr. 
                Se antal tilbageværende tokens på:{" "}
                <a
                    href="https://my.datafeedr.com/api"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    my.datafeedr.com/api
                </a>
            </p>



            <div className="d-flex justify-content-between mt-3">
                <Button variant="secondary" onClick={handleAddProduct}>
                Tilføj række
                </Button>
                <Button
                variant="primary"
                onClick={handleSubmitProducts}
                disabled={isSubmitting}
                >
                {isSubmitting ? (
                    <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Indsender...
                    </>
                ) : (
                    "Indsend"
                )}
                </Button>
            </div>
        </Modal.Body>
      </Modal>
    </>
  )
}
